import { useState, useEffect, useCallback } from "react";
import { Trophy, TrendingUp, Zap, RefreshCw, AlertCircle } from "lucide-react";
import { API } from "../App";

// ESPN API endpoints (public, no auth required)
const ESPN_APIS = {
  NFL: "https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard",
  NBA: "https://site.api.espn.com/apis/site/v2/sports/basketball/nba/scoreboard",
  MLB: "https://site.api.espn.com/apis/site/v2/sports/baseball/mlb/scoreboard",
  NHL: "https://site.api.espn.com/apis/site/v2/sports/hockey/nhl/scoreboard",
};

// Fallback mock data when no live games
const FALLBACK_GAMES = [
  { id: "no-games", sport: "INFO", team1: "No Live Games", team2: "Check Back Later", score1: "-", score2: "-", status: "⏳", isPlaceholder: true }
];

// Parse ESPN event data into our format
const parseESPNEvent = (event, sport) => {
  const competition = event.competitions?.[0];
  if (!competition) return null;

  const homeTeam = competition.competitors?.find(c => c.homeAway === "home");
  const awayTeam = competition.competitors?.find(c => c.homeAway === "away");
  
  if (!homeTeam || !awayTeam) return null;

  const status = event.status?.type;
  const statusDesc = status?.description || "";
  const isLive = status?.state === "in" || statusDesc.includes("In Progress");
  const isFinal = status?.state === "post" || statusDesc === "Final";
  const isScheduled = status?.state === "pre";
  
  // Get period/quarter/inning info
  let periodInfo = "";
  if (isLive) {
    const detail = event.status?.displayClock || "";
    const period = event.status?.period || "";
    if (sport === "NFL" || sport === "NBA") {
      periodInfo = `Q${period} ${detail}`;
    } else if (sport === "NHL") {
      periodInfo = `P${period} ${detail}`;
    } else if (sport === "MLB") {
      const inningState = event.status?.type?.shortDetail || "";
      periodInfo = inningState;
    }
  } else if (isFinal) {
    periodInfo = "Final";
  } else if (isScheduled) {
    // Show game time
    const gameDate = new Date(event.date);
    periodInfo = gameDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return {
    id: event.id,
    sport,
    team1: homeTeam.team?.abbreviation || homeTeam.team?.shortDisplayName || "HOME",
    team2: awayTeam.team?.abbreviation || awayTeam.team?.shortDisplayName || "AWAY",
    score1: homeTeam.score || "0",
    score2: awayTeam.score || "0",
    status: periodInfo,
    isLive,
    isFinal,
    isScheduled,
    fullName: event.shortName || `${homeTeam.team?.displayName} vs ${awayTeam.team?.displayName}`
  };
};

const SportsScoreTicker = ({ roomId }) => {
  const [games, setGames] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const isRacingRoom = roomId === "auto-racing" || roomId === "sprint-car-racing";
  const isSportsRoom = roomId === "sports" || roomId === "sports-talk";
  const shouldShow = isRacingRoom || isSportsRoom;

  // Fetch live scores from ESPN
  const fetchLiveScores = useCallback(async () => {
    if (!isSportsRoom) return;
    
    try {
      const allGames = [];
      
      // Fetch from all sports APIs in parallel
      const responses = await Promise.allSettled(
        Object.entries(ESPN_APIS).map(async ([sport, url]) => {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`${sport} API error`);
          const data = await response.json();
          return { sport, events: data.events || [] };
        })
      );

      // Process successful responses
      responses.forEach(result => {
        if (result.status === "fulfilled") {
          const { sport, events } = result.value;
          events.forEach(event => {
            const parsed = parseESPNEvent(event, sport);
            if (parsed) {
              allGames.push(parsed);
            }
          });
        }
      });

      // Sort: Live games first, then scheduled, then final
      allGames.sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        if (a.isScheduled && !b.isScheduled) return -1;
        if (!a.isScheduled && b.isScheduled) return 1;
        return 0;
      });

      // Filter: Only show live games or upcoming games (not old finals)
      const relevantGames = allGames.filter(g => g.isLive || g.isScheduled || g.isFinal);
      
      // Take top 10 games max
      const finalGames = relevantGames.slice(0, 10);
      
      if (finalGames.length > 0) {
        setGames(finalGames);
        setError(null);
      } else {
        setGames(FALLBACK_GAMES);
      }
      
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch sports scores:", err);
      setError("Unable to load live scores");
      setGames(FALLBACK_GAMES);
      setLoading(false);
    }
  }, [isSportsRoom]);

  // Initial fetch and periodic refresh (every 30 seconds for live data)
  useEffect(() => {
    if (!shouldShow || !isSportsRoom) return;
    
    fetchLiveScores();
    const interval = setInterval(fetchLiveScores, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [shouldShow, isSportsRoom, fetchLiveScores]);

  // Scroll through games
  useEffect(() => {
    if (!shouldShow || games.length <= 1) return;
    
    const scrollInterval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % games.length);
    }, 4000);

    return () => clearInterval(scrollInterval);
  }, [shouldShow, games.length]);

  // Early return AFTER all hooks
  if (!shouldShow || !isSportsRoom) return null;

  const hasLiveGames = games.some(g => g.isLive);

  return (
    <div className="retro-window mb-2" data-testid="sports-ticker">
      <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white px-2 py-1 flex items-center gap-2">
        {loading ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : hasLiveGames ? (
          <Zap className="w-4 h-4 animate-pulse text-yellow-300" />
        ) : (
          <Trophy className="w-4 h-4" />
        )}
        <span className="font-bold text-xs">
          {hasLiveGames ? "🏆 LIVE SCORES" : "🏆 SPORTS SCORES"}
        </span>
        
        {error ? (
          <div className="flex-1 flex items-center justify-center gap-2 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{error}</span>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <div 
              className="flex transition-transform duration-500"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {games.map((game) => (
                <div key={game.id} className="min-w-full flex items-center justify-center gap-4 text-xs">
                  {game.isPlaceholder ? (
                    <span className="opacity-75">{game.team1} - {game.team2}</span>
                  ) : (
                    <>
                      <span className={`px-1 rounded ${game.isLive ? 'bg-red-500 animate-pulse' : 'bg-white/20'}`}>
                        {game.sport}
                      </span>
                      <span className="font-bold">{game.team1}</span>
                      <span className={`font-bold ${game.isLive ? 'text-yellow-300' : 'text-white'}`}>
                        {game.score1} - {game.score2}
                      </span>
                      <span className="font-bold">{game.team2}</span>
                      <span className={`text-xs ${game.isLive ? 'bg-green-500/50 px-1 rounded' : 'opacity-75'}`}>
                        {game.status}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {lastUpdate && (
          <span className="text-[10px] opacity-50" title={`Last updated: ${lastUpdate.toLocaleTimeString()}`}>
            {hasLiveGames ? "LIVE" : "Updated"}
          </span>
        )}
      </div>
    </div>
  );
};

// Betting odds - these remain simulated as real odds require paid APIs
const BETTING_ODDS = [
  { game: "Today's Games", spread: "Check sportsbooks", overUnder: "for odds", moneyline: "", isInfo: true },
];

const BettingOddsTicker = ({ roomId }) => {
  const [games, setGames] = useState([]);
  const [currentOdds, setCurrentOdds] = useState(0);
  const [odds, setOdds] = useState(BETTING_ODDS);

  const isRacingRoom = roomId === "auto-racing" || roomId === "sprint-car-racing";
  const isSportsRoom = roomId === "sports" || roomId === "sports-talk";
  const shouldShow = isRacingRoom || isSportsRoom;

  // Fetch games to build odds display
  useEffect(() => {
    if (!shouldShow || !isSportsRoom) return;
    
    const fetchGamesForOdds = async () => {
      try {
        const responses = await Promise.allSettled(
          Object.entries(ESPN_APIS).map(async ([sport, url]) => {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`${sport} API error`);
            const data = await response.json();
            return { sport, events: data.events || [] };
          })
        );

        const gameOdds = [];
        responses.forEach(result => {
          if (result.status === "fulfilled") {
            const { sport, events } = result.value;
            events.slice(0, 2).forEach(event => {
              const parsed = parseESPNEvent(event, sport);
              if (parsed && (parsed.isLive || parsed.isScheduled)) {
                // Generate simulated betting lines based on scores
                const spread = parsed.isLive 
                  ? `${parsed.team1} ${(Math.random() * 7 - 3.5).toFixed(1)}`
                  : `${parsed.team1} ${(Math.random() * 10 - 5).toFixed(1)}`;
                const total = sport === "NBA" ? Math.floor(200 + Math.random() * 40) :
                             sport === "NFL" ? Math.floor(40 + Math.random() * 15) :
                             sport === "NHL" ? (5 + Math.random() * 2).toFixed(1) :
                             Math.floor(7 + Math.random() * 4);
                             
                gameOdds.push({
                  game: `${parsed.team1} vs ${parsed.team2}`,
                  spread,
                  overUnder: `O/U ${total}`,
                  moneyline: `${parsed.team1} ${Math.random() > 0.5 ? '-' : '+'}${Math.floor(100 + Math.random() * 100)}`,
                  sport: parsed.sport
                });
              }
            });
          }
        });

        if (gameOdds.length > 0) {
          setOdds(gameOdds);
        }
      } catch (err) {
        console.error("Failed to fetch odds:", err);
      }
    };

    fetchGamesForOdds();
    const interval = setInterval(fetchGamesForOdds, 60000); // Refresh every minute
    
    return () => clearInterval(interval);
  }, [shouldShow, isSportsRoom]);

  // Scroll through odds
  useEffect(() => {
    if (!shouldShow || odds.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentOdds(prev => (prev + 1) % odds.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [shouldShow, odds.length]);

  // Early return AFTER all hooks
  if (!shouldShow || !isSportsRoom) return null;

  const currentOdd = odds[currentOdds];

  return (
    <div className="retro-window mb-2" data-testid="odds-ticker">
      <div className="bg-gradient-to-r from-green-800 via-green-700 to-green-800 text-white px-2 py-1">
        <div className="flex items-center gap-2 text-xs">
          <TrendingUp className="w-4 h-4" />
          <span className="font-bold">BETTING LINES</span>
          <div className="flex-1 flex items-center justify-center gap-3">
            {currentOdd.isInfo ? (
              <span className="opacity-75">{currentOdd.game} {currentOdd.spread} {currentOdd.overUnder}</span>
            ) : (
              <>
                <span className="bg-white/10 px-1 rounded text-[10px]">{currentOdd.sport}</span>
                <span className="font-bold text-yellow-300">{currentOdd.game}</span>
                <span className="bg-white/20 px-2 rounded">Spread: {currentOdd.spread}</span>
                <span className="bg-white/20 px-2 rounded">{currentOdd.overUnder}</span>
                <span className="bg-white/20 px-2 rounded">ML: {currentOdd.moneyline}</span>
              </>
            )}
          </div>
          <span className="text-[10px] opacity-50">*Simulated</span>
        </div>
      </div>
    </div>
  );
};

// Racing ticker — live results/standings scraped daily from official series sites.
const RacingTicker = ({ roomId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sources, setSources] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isRacingRoom = roomId === "auto-racing" || roomId === "sprint-car-racing";

  const fetchTicker = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/racing/ticker?room_id=${encodeURIComponent(roomId)}`);
      if (!res.ok) throw new Error("Failed to load results");
      const data = await res.json();
      setSources(data.sources || []);
      setLastUpdated(data.last_updated || null);
      setError(null);
    } catch (err) {
      setError("Results temporarily unavailable");
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (!isRacingRoom) return;
    fetchTicker();
    // Re-check hourly; backend only re-scrapes ~once a day.
    const interval = setInterval(fetchTicker, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isRacingRoom, fetchTicker]);

  useEffect(() => {
    if (!isRacingRoom || sources.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % sources.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [isRacingRoom, sources.length]);

  if (!isRacingRoom) return null;

  const current = sources[currentIndex] || null;
  const updatedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString([], { month: "short", day: "numeric" })
    : null;

  return (
    <div className="retro-window mb-2" data-testid="racing-ticker">
      <div className="bg-gradient-to-r from-orange-700 via-orange-600 to-orange-700 text-white px-2 py-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-lg">🏁</span>
          <span className="font-bold text-[11px] sm:text-xs">SPRINT CAR RESULTS</span>
          {current && <span className="text-[10px] bg-black/30 px-2 rounded truncate max-w-[45%]">{current.series}</span>}
          {current && <span className="text-[10px] truncate">{current.event}</span>}
          {updatedLabel && <span className="text-[9px] opacity-60 ml-auto">Updated {updatedLabel}</span>}
        </div>

        {loading && sources.length === 0 ? (
          <div className="flex items-center gap-2 text-[11px] bg-black/20 rounded px-2 py-1">
            <RefreshCw className="w-3 h-3 animate-spin" /> Loading live results…
          </div>
        ) : error && sources.length === 0 ? (
          <div className="flex items-center gap-2 text-[11px] bg-black/20 rounded px-2 py-1">
            <AlertCircle className="w-3 h-3" /> {error}
          </div>
        ) : current && current.type === "standings" ? (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] bg-black/20 rounded px-2 py-1" data-testid="racing-standings">
            {current.entries.map((f) => (
              <span key={f.pos} className={`flex items-center gap-1 ${
                f.pos === 1 ? 'text-yellow-300 font-bold' :
                f.pos === 2 ? 'text-gray-300' :
                f.pos === 3 ? 'text-orange-300' : 'text-white/80'
              }`}>
                <span className="bg-white/20 px-1 rounded">{f.pos}</span>
                <span>#{f.car}</span>
                <span>{f.driver}</span>
                {f.pos <= 3 && <span>{f.pos === 1 ? '🥇' : f.pos === 2 ? '🥈' : '🥉'}</span>}
              </span>
            ))}
          </div>
        ) : current ? (
          <div className="flex flex-col gap-0.5 text-[10px] bg-black/20 rounded px-2 py-1" data-testid="racing-recaps">
            {current.entries.slice(0, 5).map((e, i) => (
              current.type === "link" ? (
                <a key={i} href={current.url} target="_blank" rel="noopener noreferrer"
                   className="text-yellow-200 hover:underline">{e.headline}</a>
              ) : (
                <span key={i} className="text-white/90 truncate">🏆 {e.headline}</span>
              )
            ))}
          </div>
        ) : null}
      </div>
      {sources.length > 1 && (
        <div className="flex justify-center gap-1 py-1 bg-gray-800">
          {sources.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              aria-label={`Show source ${i + 1}`}
              className={`w-2 h-2 rounded-full transition-colors ${i === currentIndex ? 'bg-orange-500' : 'bg-gray-600'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Combined component for easy import
const SportsTickers = ({ roomId }) => {
  const isRacingRoom = roomId === "auto-racing" || roomId === "sprint-car-racing";
  const isSportsRoom = roomId === "sports" || roomId === "sports-talk";

  if (!isRacingRoom && !isSportsRoom) return null;

  return (
    <div>
      {isSportsRoom && <SportsScoreTicker roomId={roomId} />}
      {isSportsRoom && <BettingOddsTicker roomId={roomId} />}
      {isRacingRoom && <RacingTicker roomId={roomId} />}
    </div>
  );
};

export default SportsTickers;
export { SportsScoreTicker, BettingOddsTicker, RacingTicker };
